import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TablePagination from '@mui/material/TablePagination';
import ViewIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  SelectChangeEvent
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { usersAPI, User as APIUser, UserType } from '../services/api';
import { API_BASE_URL } from '../services/api';

interface Country {
  id: number;
  name: string;
  phonecode: string;
  sortname: string;
  status: number;
  created_at: string;
  updated_at: string;
}

type FormData = {
  name: string;
  email: string;
  mobile: string;
  country_code?: number;
  status: 'active' | 'inactive' | 'suspended';
  user_type: UserType;
  customer_type?: 'Individual' | 'Business';
};

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning';
};

const CustomerMasters: React.FC = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editingCustomer, setEditingCustomer] = useState<APIUser | null>(null);
  const [customers, setCustomers] = useState<APIUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const ucfirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    mobile: '',
    country_code: undefined,
    status: 'active',
    user_type: 'customer' as UserType,
    customer_type: 'Individual'
  });
    const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [countries, setCountries] = useState<Country[]>([]);

  // Fetch data on component mount and when pagination changes
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/country/list`);
        if (!response.ok) throw new Error('Failed to fetch countries');
        
        const result = await response.json();
        
        if (result.status === 'success' && Array.isArray(result.data)) {
          // Create a map of the preferred country codes for easy lookup
          const preferredCodes = ['32', '33', '49'];
          const preferredCountries: Country[] = [];
          const otherCountries: Country[] = [];
          
          // Process all countries
          result.data.forEach((country: any) => {
            const phonecode = country.phone_code.startsWith('+') 
              ? country.phone_code 
              : `+${country.phone_code}`;
              
            const countryItem = {
              id: country.id,
              name: country.country_name,
              phonecode,
              sortname: country.country_code,
              status: country.status,
              created_at: country.created_at || new Date().toISOString(),
              updated_at: country.updated_at || new Date().toISOString()
            };
            
            // Check if this is one of our preferred countries
            const phoneCode = country.phone_code.replace('+', '');
            if (preferredCodes.includes(phoneCode)) {
              preferredCountries.push(countryItem);
            } else {
              otherCountries.push(countryItem);
            }
          });
          
          // Sort preferred countries in the order: 32, 33, 49
          const sortedPreferred = preferredCountries.sort((a, b) => {
            const order: Record<string, number> = { '32': 1, '33': 2, '49': 3 };
            const aCode = a.phonecode.replace('+', '');
            const bCode = b.phonecode.replace('+', '');
            return (order[aCode] || 0) - (order[bCode] || 0);
          });
          
          // Sort other countries alphabetically
          const sortedOther = otherCountries.sort((a, b) => a.name.localeCompare(b.name));
          
          // Combine with preferred countries first
          const allCountries = [...sortedPreferred, ...sortedOther];
          setCountries(allCountries);
          
          // Set default country code to Belgium (+32)
          const belgium = allCountries.find(c => c.phonecode === '+32');
          if (belgium && !formData.country_code) {
            setFormData(prev => ({
              ...prev,
              country_code: belgium.id
            }));
          }
        } else {
          throw new Error('Invalid response format from countries API');
        }
      } catch (err) {
        console.error('Error fetching countries:', err);
        // Fallback to a few common countries if the API call fails
        const fallbackCountries: Country[] = [
          { 
            id: 1, 
            name: 'Belgium', 
            phonecode: '+32', 
            sortname: 'BE', 
            status: 1, 
            created_at: new Date().toISOString(), 
            updated_at: new Date().toISOString() 
          },
          { 
            id: 2, 
            name: 'France', 
            phonecode: '+33', 
            sortname: 'FR', 
            status: 1, 
            created_at: new Date().toISOString(), 
            updated_at: new Date().toISOString() 
          },
          { 
            id: 3, 
            name: 'Germany', 
            phonecode: '+49', 
            sortname: 'DE', 
            status: 1, 
            created_at: new Date().toISOString(), 
            updated_at: new Date().toISOString() 
          },
          { 
            id: 4, 
            name: 'India', 
            phonecode: '+91', 
            sortname: 'IN', 
            status: 1, 
            created_at: new Date().toISOString(), 
            updated_at: new Date().toISOString() 
          }
        ];
        
        setCountries(fallbackCountries);
        
        // Set default to Belgium if not set
        if (!formData.country_code) {
          setFormData(prev => ({
            ...prev,
            country_code: 1 // Belgium's ID in fallback
          }));
        }
        
        setSnackbar({
          open: true,
          message: 'Using limited country list. Could not fetch full country data.',
          severity: 'warning'
        });
      }
    };
    
    fetchCountries();

    const fetchCustomers = async () => {
      try {
        setLoading(true);

        // Get structured data directly from usersAPI
        const { users, pagination } = await usersAPI.getAll('customer', page, rowsPerPage);

        setTotalRecords(pagination.total || 0);

        const mappedCustomers = (users || []).map((user: APIUser) => ({
          ...user,
          user_type: (user.user_type || 'customer') as UserType,
          status: (user.status || 'active') as 'active' | 'inactive' | 'suspended',
          email: user.email || '',
          mobile: user.mobile || '',
          email_verified: user.email_verified || false,
          mobile_verified: user.mobile_verified || false,
          created_at: user.created_at || new Date().toISOString(),
          updated_at: user.updated_at || new Date().toISOString()
        }));

        setCustomers(mappedCustomers);
      setError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch customers';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  fetchCustomers();
}, [page, rowsPerPage]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCountryChange = (e: SelectChangeEvent<number>) => {
    setFormData(prev => ({
      ...prev,
      country_code: Number(e.target.value)
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent<number | string>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (customer?: APIUser) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        email: customer.email || '',
        mobile: customer.mobile || '',
        country_code: customer.country_id || 32, // Default to 32 (Belgium) if no country_id
        status: customer.status as 'active' | 'inactive' | 'suspended',
        user_type: (customer.user_type || 'customer') as UserType,
        customer_type: customer.customer_type as 'Individual' | 'Business' || 'Individual'
      });
    } else {
      // For new customer, set default values
      const belgium = countries.find(c => c.phonecode === '+32');
      setFormData({
        name: '',
        email: '',
        mobile: '',
        country_code: belgium?.id,
        status: 'active',
        user_type: 'customer' as UserType,
        customer_type: 'Individual'
      });
      setEditingCustomer(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    const belgium = countries.find(c => c.phonecode === '+32');
    setFormData({
      name: '',
      email: '',
      mobile: '',
      country_code: belgium?.id,
      status: 'active',
      user_type: 'customer' as UserType,
      customer_type: 'Individual'
    });
    setEditingCustomer(null);
    setFormErrors({});
  };

  const toggleStatus = async (id: number, currentStatus: string) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

      // Find the customer to update
      const customerToUpdate = customers.find(c => c.user_id === id);
      if (!customerToUpdate) {
        throw new Error('Customer not found');
      }

      // Update the status with all required fields
      await usersAPI.update(id, {
        name: customerToUpdate.name,
        email: customerToUpdate.email || '',
        mobile: customerToUpdate.mobile || '',
        status: newStatus,
        user_type: customerToUpdate.user_type
      });

      showSnackbar(`Customer marked as ${newStatus}`, 'success');

      // Update the local state
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.user_id === id
            ? {
                ...customer,
                status: newStatus as 'active' | 'inactive' | 'suspended'
              }
            : customer
        )
      );
    } catch (err) {
      console.error('Error toggling status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer status';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Prepare the base user data according to the API expectations
      const baseUserData = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        country_code: formData.country_code, // Add country_code here
        status: formData.status,
        user_type: 'customer' as UserType
      };

      if (editingCustomer) {
        // For updates, include all necessary fields including customer_type
        const updateData = {
          ...baseUserData,
          customer_type: formData.customer_type,
          email_verified: editingCustomer.email_verified,
          mobile_verified: editingCustomer.mobile_verified
        };
        await usersAPI.update(editingCustomer.user_id, updateData);
        showSnackbar('Customer updated successfully', 'success');
      } else {
        // For new users, create a new object with password included
        // The API will handle password hashing
        const newUserData = {
          ...baseUserData,
          // The API expects password_hash but handles the plain password
          password: 'defaultPassword123!'
        };

        await usersAPI.create(newUserData);
        showSnackbar('Customer created successfully', 'success');
      }

      // Refresh the customers list
      const response = await usersAPI.getAll('customer');
      setCustomers(Array.isArray(response) ? response : []);

      // Reset form and close dialog
      setFormData({
        name: '',
        email: '',
        mobile: '',
        status: 'active',
        user_type: 'customer' as UserType,
        customer_type: 'Individual'
      });
      setOpenDialog(false);
    } catch (err) {
      console.error('Error saving customer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save customer';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        setLoading(true);
        await usersAPI.delete(id);
        showSnackbar('Customer deleted successfully', 'success');

        // Refresh the customers list
        const response = await usersAPI.getAll('customer');
        const data = Array.isArray(response) ? response : [];
        setCustomers(data);
      } catch (err) {
        console.error('Error deleting customer:', err);
        showSnackbar('Failed to delete customer', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          
          <Typography variant="h4"  fontWeight="bold">Customer Management</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Add Customer
        </Button>
      </Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      <Paper sx={{ p: { xs: 0, sm: 0 } }}>
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                {/*<TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Verified</TableCell>
                <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>*/}
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No customers found. Click "Add New Customer" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.user_id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {customer.name}
                        </Typography>
                        {customer.customer_type && (
                          <Typography variant="caption" color="text.secondary">
                            {customer.customer_type}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {customer.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{customer.mobile}</TableCell>
                    <TableCell>
                      <Chip
                        label={customer.customer_type || 'Individual'}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={customer.status && ucfirst(customer.status)}
                        size="small"
                        color={customer.status === 'active' ? 'success' : customer.status === 'suspended' ? 'error' : 'default'}
                        onClick={() => toggleStatus(customer.user_id, customer.status)}
                        sx={{ cursor: 'pointer', minWidth: 80 }}
                      />
                    </TableCell>
                    {/*<TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {customer.email_verified && customer.mobile_verified ? '✓ Both' :
                       customer.email_verified ? '✓ Email' :
                       customer.mobile_verified ? '✓ Mobile' : '✗ None'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {new Date(customer.created_at).toLocaleDateString()}
                    </TableCell>*/}
                    <TableCell align="right">
                      <IconButton
                        component={Link}
                        to={`/customers/${customer.user_id}`}
                        color="primary"
                        size="small"
                        title="View Customer"
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDialog(customer)}
                        color="primary"
                        size="small"
                        disabled={loading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(customer.user_id)}
                        color="error"
                        size="small"
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          

<TablePagination
  component="div"
  count={totalRecords}
  page={page - 1}
  onPageChange={(event, newPage) => setPage(newPage + 1)}
  rowsPerPage={rowsPerPage}
  onRowsPerPageChange={(e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(1);
  }}
  rowsPerPageOptions={[5, 10, 25]}
/>

        </TableContainer>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth margin="normal" required>
                      <InputLabel id="country-label">Country</InputLabel>
                      <Select
                        labelId="country-label"
                        name="country_code"
                        value={formData.country_code || ''}
                        onChange={handleSelectChange}
                        label="Country"
                        sx={{
                          '& .MuiSelect-select': {
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                          }
                        }}
                      >
                        <MenuItem value="">Select Country</MenuItem>
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.name} ({country.phonecode})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      margin="normal"
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel id="customer-type-label">Customer Type</InputLabel>
                  <Select
                    labelId="customer-type-label"
                    name="customer_type"
                    value={formData.customer_type}
                    label="Customer Type"
                    onChange={handleSelectChange}
                    required
                  >
                    <MenuItem value="Individual">Individual</MenuItem>
                    <MenuItem value="Business">Business</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={(e: SelectChangeEvent<string>) => handleSelectChange(e)}
                    required
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {editingCustomer ? 'Update' : 'Add'} Customer
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default CustomerMasters;
